import { Component, type ReactNode, type ErrorInfo } from 'react';
import { trackError } from '../telemetry/telemetryEvents';
import ErrorScreen from './common/ErrorScreen';
import { errorDev } from '@/logging/devConsole';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component that catches JavaScript errors and tracks them via telemetry
 */
class TelemetryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state to trigger error UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details to console (only in development)
    errorDev('TelemetryErrorBoundary caught an error:', error, errorInfo);
    
    // Track the component error via telemetry
    trackError('component-error', error);
    
    // You could also send error details to an external logging service here
    // logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI or use the provided fallback
      return this.props.fallback || <ErrorScreen />;
    }

    return this.props.children;
  }
}

export default TelemetryErrorBoundary;